import { Component, Prop, Vue } from 'vue-property-decorator'
import { KTable } from '@kwixeo/table-vue'
import { mapGetters } from 'vuex'

@Component({
  components: {
    KTable
  },
  computed: {
    ...mapGetters('user', ['can'])
  }
})
export default class ElementSalesList extends Vue {
  can!: (key: string, level: number) => boolean

  @Prop({ required: true }) documentType!: string
  @Prop({ required: true }) type!: string
  @Prop({ required: true }) id!: number

  get ajaxDatas (): KwixeoTable.Object {
    return {
      include: ['vat_rate', this.documentType, this.documentType + '.customer', this.documentType + '.contact']
    }
  }

  get ajaxUrl (): string {
    return '/catalog/' + this.type + '/' + this.id + '/' + this.documentType
  }

  get columns (): KwixeoTable.Columns {
    const columns: KwixeoTable.Columns = [
      {
        data: this.documentType + '.reference',
        name: this.$t('catalog.sales.list.documentReference')
      },
      {
        data: this.documentType + '.date',
        name: this.$t('catalog.sales.list.date'),
        render: 'date',
      },
      {
        data: this.documentType + '.customer.name',
        name: this.$t('catalog.sales.list.customer'),
        visible: false
      },
      {
        data: this.documentType + '.contact.fullname',
        name: this.$t('catalog.sales.list.contact'),
        visible: false
      },

      {
        data: 'unit_price_ht',
        name: this.$t('catalog.sales.list.unitPriceHt'),
        render: 'currency'
      },
      {
        data: 'unit_price_tva',
        name: this.$t('catalog.sales.list.unitPriceTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'unit_price_ttc',
        name: this.$t('catalog.sales.list.unitPriceTtc'),
        render: 'currency',
        visible: false
      }
    ]

    if (this.can('sales-margin', 1)) {
      columns.push(...[
        {
          data: 'catalog_costs_u',
          name: this.$t('catalog.sales.list.catalogCostU'),
          render: 'currency',
          visible: false
        },
        {
          data: 'various_costs_u',
          name: this.$t('catalog.sales.list.variousCostU'),
          render: 'currency',
          visible: false
        },
        {
          data: 'total_costs_u',
          name: this.$t('catalog.sales.list.totalCostU'),
          render: 'currency',
          visible: false
        },
        {
          data: 'hourly_person_cost',
          name: this.$t('catalog.sales.list.hourlyPersonCost'),
          render: 'currency',
          visible: false
        }
      ])
    }

    columns.push(...[
      {
        data: 'number_people_needed',
        name: this.$t('catalog.sales.list.numberPeopleNeeded'),
        render: 'currency',
        visible: false
      },
      {
        data: 'duration',
        name: this.$t('catalog.sales.list.duration'),
        visible: false,
        render: (data: number, row: KwixeoTable.Object) => data + ' ' + this.$tc('interface.duration.' + row.duration_type, data)
      }
    ])

    if (this.can('sales-margin', 1)) {
      columns.push(...[
        {
          data: 'workforce_costs_u',
          name: this.$t('catalog.sales.list.workforceCostsU'),
          render: 'currency',
          visible: false
        },
        {
          data: 'total_charge_u',
          name: this.$t('catalog.sales.list.totalChargeU'),
          render: 'currency',
          visible: false
        },
        {
          data: 'gross_margin_amount_u',
          name: this.$t('catalog.sales.list.grossMarginAmountU'),
          render: 'currency',
          visible: false
        }
      ])
    }

    columns.push(...[
      {
        data: 'quantity',
        name: this.$t('catalog.sales.list.quantity'),
        render: 'amount'
      },
      {
        data: 'secondary_quantity',
        name: this.$t('catalog.sales.list.secondaryQuantity'),
        render: 'amount',
        visible: false
      },
      {
        data: 'total_quantity',
        name: this.$t('catalog.sales.list.totalQuantity'),
        render: 'amount',
        visible: false
      },
      {
        data: 'vat_rate.rate',
        name: this.$t('catalog.sales.list.vatRate'),
        visible: false
      },
      {
        data: 'total_ht',
        name: this.$t('catalog.sales.list.totalHt'),
        render: 'currency'
      },
      {
        data: 'total_tva',
        name: this.$t('catalog.sales.list.totalTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'total_ttc',
        name: this.$t('catalog.sales.list.totalTtc'),
        render: 'currency',
        visible: false
      }
    ])

    if (this.can('sales-margin', 1)) {
      columns.push(...[
        {
          data: 'total_catalog_costs',
          name: this.$t('catalog.sales.list.totalCatalogCosts'),
          render: 'currency',
          visible: false
        },
        {
          data: 'total_various_costs',
          name: this.$t('catalog.sales.list.totalVariousCosts'),
          render: 'currency',
          visible: false
        },
        {
          data: 'total_costs',
          name: this.$t('catalog.sales.list.totalCosts'),
          render: 'currency',
          visible: false
        }
      ])
    }

    columns.push(...[
      {
        data: 'total_duration_hour',
        name: this.$t('catalog.sales.list.totalDurationHour'),
        render: 'currency',
        visible: false
      }
    ])

    if (this.can('sales-margin', 1)) {
      columns.push(...[
        {
          data: 'total_workforce',
          name: this.$t('catalog.sales.list.totalWorkforce'),
          render: 'currency',
          visible: false
        },
        {
          data: 'total_charge',
          name: this.$t('catalog.sales.list.totalCharge'),
          render: 'currency',
          visible: false
        },
        {
          data: 'gross_margin_amount',
          name: this.$t('catalog.sales.list.grossMarginAmount'),
          render: 'currency',
          visible: false
        },
        {
          data: 'gross_margin_rate',
          name: this.$t('catalog.sales.list.grossMarginRate'),
          render: 'pourcentage',
          visible: false
        },
        {
          data: 'markup_rate',
          name: this.$t('catalog.sales.list.markupRate'),
          render: 'pourcentage',
          visible: false
        }
      ])
    }

    return columns
  }

  get filters (): KwixeoTable.FiltersOption {
    return [
      {
        type: 'period',
        label: this.$t('catalog.sales.filter.period'),
        name: 'date_since',
        name2: 'date_until'
      },
      {
        type: 'select',
        label: this.$t('catalog.sales.filter.customer'),
        name: 'customer',
        multiple: true,
        ajax: '/customer?sort=name&filter[is_customer]=1',
        optionLabel: 'name',
        value: 'id'
      }
    ]
  }

  private getDataKey (data: KwixeoTable.Data): string {
    return data[this.documentType].id
  }

  private rowClick (data: KwixeoInterface.GenericObject): void {
    this.$router.push({ name: this.documentType + '-item', params: { id: data[this.documentType].id } })
  }
}
